/**
 * CasePage, the chat-first case workspace (2026-07-24 revamp).
 *
 * The customer-facing single-case view at /case/:billId. F's brief: the
 * conversation IS the page, especially on mobile (most customers are on
 * phones). Everything else supports it:
 *
 *   - Compact sticky header: case name, bill amount, stage pill, and a
 *     hairline 4-segment progress bar. The old dark hero block is gone.
 *   - The thread reads in true chronological order (oldest at the top,
 *     newest at the bottom) and auto-scrolls to the latest, like every
 *     messenger the customer already knows.
 *   - The thread is PEOPLE ONLY (2026-07-24, F): status updates and
 *     system notices don't render in the conversation at all — the
 *     stage pill / progress rail / Details sheet carry case progress.
 *     A realtime status event refreshes the bill row so those move
 *     live instead.
 *   - The composer is pinned to the bottom, quick-reply chips scroll
 *     horizontally above it.
 *   - Case progress + documents live in a right rail on desktop and
 *     behind a "Details" bottom sheet on mobile.
 *
 * Closed cases (2026-08-27 redesign) stay on this same page: the final
 * result renders first, the full conversation is preserved below it, and
 * the composer keeps working so the thread never dies.
 */

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { prepareImageForUpload } from '../lib/imageUpload';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/context/ToastContext';
import { useBillRealtime, type ChatMessageRow } from '../hooks/useBillRealtime';
import AppSidebar from '../components/AppSidebar';
import ResultBlock from '../components/ResultBlock';
import Modal from '../components/Modal';
import { Check, Send, Paperclip, ArrowLeft, ChevronDown, FileText, Download, Plus, X as XIcon, FolderOpen, Eye, ExternalLink, Sparkles } from 'lucide-react';
import type { Bill, CaseStage, CustomerRequest } from '../types/bill';
import {
  CASE_STAGES,
  CASE_STAGE_LABELS,
  CASE_WAITING_ON_LABELS,
  CASE_OUTCOME_LABELS,
  RESOLUTION_PATH_LABELS,
} from '../lib/case-state';
import {
  phCaseViewed,
  phCaseMessageSent,
  phDocumentDownloaded,
  phFailure,
} from '../lib/posthog';
import { caseTitle } from '../lib/case-summary';
import { useAsk } from '../lib/ask';
import '@styles/pages/case-page.css';

type FeedEvent = { kind: 'message'; row: ChatMessageRow; ts: string };

function formatTime(ts: string): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
}

/* Bucket label for date dividers in the feed.
   "Today" / "Yesterday" / "May 12" / "May 12, 2024" depending on age. */
function dayBucket(ts: string): string {
  const d = new Date(ts);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (isSameDay(d, now)) return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
}

/* Legacy 4-stage rail, rendered only when the bill predates migration
   084 (case_stage null — e.g. this deploy reaching prod before the
   migration runs). Once every row carries case_stage, this fallback
   and the status-based mapping below can go. */
const STAGES = ['Case filed', 'Investigation', 'Negotiation', 'Closed'];

/* Case state v2: the five stages the progress bar renders, labelled
   with the shared customer wording. Blockers no longer appear here —
   "waiting on you" is the Needed-from-you card, not a stage. */
const STAGES_V2: { key: CaseStage; label: string }[] = CASE_STAGES.map((key) => ({
  key,
  label: CASE_STAGE_LABELS[key],
}));

export default function CasePage() {
  const { id: billId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const ask = useAsk();

  const [bill, setBill] = useState<Bill | null>(null);
  const [messages, setMessages] = useState<ChatMessageRow[]>([]);
  const [specialistName, setSpecialistName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  /* Case state v2 (migration 084): the active "needed from you"
     request. Fails soft — before the migration runs, the page renders
     exactly as it did without it. */
  const [activeRequest, setActiveRequest] = useState<CustomerRequest | null>(null);

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  /* Mobile "Details" bottom sheet (progress + documents). */
  const [sheetOpen, setSheetOpen] = useState(false);
  /* Closed cases: the five completed steps collapse behind the Case
     progress header (they all read "Done" once it's over) — the outcome
     facts are the summary, the journey is one click away. Open cases
     keep the steps always visible. */
  const [journeyOpen, setJourneyOpen] = useState(false);

  /* ⚠️ 2026-08-08: this page — the case workspace where customers actually
     live after purchase — had zero analytics calls in 1100+ lines. Combined
     with SPA route navigations not producing $pageview (fixed in
     lib/posthog.ts), the entire post-purchase surface was invisible except
     via session replay. Fires once per bill. */
  const caseViewTracked = useRef<string | null>(null);
  useEffect(() => {
    if (!bill?.id) return;
    if (caseViewTracked.current === bill.id) return;
    caseViewTracked.current = bill.id;
    phCaseViewed({ billId: bill.id, status: bill.status });
  }, [bill?.id, bill?.status]);

  /* What Agent Loop did on this bill: counts only (migration 101's
     agent_loop_receipt hands the customer nothing but numbers for their
     own bill). Null until the engine has read it. Re-read when the
     status moves, which is when a run has just finished. */
  const [receipt, setReceipt] = useState<{ read_at: string; documents: number; line_items: number; checks: number | null; drafted_at: string | null } | null>(null);
  useEffect(() => {
    if (!bill?.id) return;
    let cancelled = false;
    supabase
      .rpc('agent_loop_receipt', { p_bill_id: bill.id })
      .then(({ data }) => {
        if (cancelled) return;
        const row = Array.isArray(data) ? data[0] : data;
        setReceipt(row && row.read_at ? row : null);
      });
    return () => { cancelled = true; };
  }, [bill?.id, bill?.status]);

  /* Inline edit state for chat messages — id of the message currently
     being edited + its draft text. RLS gates server-side; the UI just
     reveals the affordance for own messages younger than 15 minutes. */
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  /* Documents — extra files attached AFTER the initial bill upload.
     Stored in bills.attachments jsonb (migration 049). The shape mirrors
     chat_messages.attachments so the storage/upload flow can be reused
     later if we consolidate. */
  type Attachment = {
    url: string;
    name: string;
    type: string;
    uploaded_at: string;
  };
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = useRef<HTMLInputElement>(null);

  /* Auto-scroll the thread to the newest entry. */
  const threadRef = useRef<HTMLDivElement>(null);

  /* Fetch the active request for this bill. customer_requests is read
     directly, its RLS scopes rows to the bill's owner. Any error
     (including "084 not applied yet") leaves the page in its pre-v2
     shape rather than breaking it. The old Case history rail section
     is gone deliberately: it restated the progress rail using the
     internal event log's wording ("Status changed to Partial Win"),
     which is not customer language. */
  const loadCaseState = useCallback(async (id: string) => {
    const reqRes = await supabase
      .from('customer_requests')
      .select('*')
      .eq('bill_id', id)
      .in('status', ['open', 'response_received', 'paused'])
      .maybeSingle();
    setActiveRequest(reqRes.error ? null : ((reqRes.data as CustomerRequest | null) ?? null));
  }, []);

  /* ── Initial load ── */
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!billId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      const [billRes, messagesRes] = await Promise.all([
        supabase
          .from('bills')
          .select('*')
          .eq('id', billId)
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('chat_messages')
          .select('*')
          .eq('bill_id', billId)
          .order('created_at', { ascending: true }),
      ]);

      if (cancelled) return;

      if (!billRes.data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setBill(billRes.data as Bill);
      setMessages((messagesRes.data ?? []) as ChatMessageRow[]);

      /* v2 case state — after the bill so a slow query never delays
         first paint of the thread. Fire-and-forget into state. */
      loadCaseState(billId).catch(() => {});

      /* Read receipts: opening the thread marks the specialist's
         messages as read (RLS policy "Customers can mark messages
         read"). The old Dashboard chat modal did this; the CasePage
         rewrite dropped it, so ops-side receipts sat on "Delivered"
         forever (nothing marked read since 2026-05-18). Fire-and-forget:
         the PostgREST builder only executes once .then() is called.
         Also cancel any queued new-message email — they're reading
         in-app now. */
      const hasUnreadAdminMsg = ((messagesRes.data ?? []) as ChatMessageRow[]).some(
        (m) => m.sender_type === 'admin' && !m.read_at && m.is_internal !== true && !m.deleted_at,
      );
      if (hasUnreadAdminMsg) {
        supabase
          .from('chat_messages')
          .update({ read_at: new Date().toISOString() })
          .eq('bill_id', billId)
          .eq('sender_type', 'admin')
          .eq('is_internal', false)
          .is('deleted_at', null)
          .is('read_at', null)
          .then(({ error: readErr }) => {
            if (readErr) console.error('mark read error', readErr);
          });
        supabase
          .from('bills')
          .update({ pending_email_at: null })
          .eq('id', billId)
          .then(() => {});
      }

      /* Hydrate the attachments array from the bill row. The column is
         nullable so coerce to []. */
      const rawAttachments =
        (billRes.data as { attachments?: Attachment[] | null }).attachments ??
        [];
      setAttachments(rawAttachments);

      /* Resolve specialist name (from bills.assigned_to → admin_users.name)
         in a second query, keeps the parallel block above bounded to the
         three tables the page strictly needs, and lets the specialist
         name fail silently without blocking the page render. */
      const assignedTo = (billRes.data as Bill).assigned_to;
      if (assignedTo) {
        const { data: adminRow } = await supabase
          .from('admin_users')
          .select('name')
          .eq('id', assignedTo)
          .maybeSingle();
        if (!cancelled && adminRow?.name) {
          setSpecialistName(adminRow.name);
        }
      }

      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [billId, user, authLoading, navigate, loadCaseState]);

  /* ── Realtime: new updates and new chat messages ── */
  /* Update chips no longer render in the thread, but a bill_updates
     event usually means the status just changed — refetch the bill row
     so the stage pill and progress rail move without a reload. */
  const handleNewUpdate = useCallback(() => {
    if (!billId || !user) return;
    supabase
      .from('bills')
      .select('*')
      .eq('id', billId)
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setBill(data as Bill);
      });
    /* Stage moves, pauses and request changes all surface as
       bill_updates events, so this is the one hook that keeps the v2
       panel live too. */
    loadCaseState(billId).catch(() => {});
  }, [billId, user, loadCaseState]);
  /* No-op: bill_communication_logs (provider-side activity) is rendered
     by BillDetailModal's Case Activity tab today. Once that tab is
     folded into the unified feed we'll merge new entries in here too —
     until then the realtime subscription stays wired so the channel
     stays warm and reconnects on focus, even though we drop the
     payload on the floor. */
  const handleNewCommLog = useCallback(() => {}, []);
  const handleNewChatMessage = useCallback((m: ChatMessageRow) => {
    if (m.is_internal === true) return; // admin-only note — never surface
    setMessages((prev) => (prev.find((x) => x.id === m.id) ? prev : [...prev, m]));
    /* The thread is on screen, so an incoming specialist message is
       read the moment it lands — flip the receipt and cancel any
       queued new-message email. */
    if (m.sender_type === 'admin' && !m.read_at && billId) {
      supabase
        .from('chat_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('id', m.id)
        .is('read_at', null)
        .then(() => {});
      supabase
        .from('bills')
        .update({ pending_email_at: null })
        .eq('id', billId)
        .then(() => {});
    }
  }, [billId]);
  /* A request raised (or satisfied, or reopened) by ops writes no
     timeline row, comm log or chat message, so none of the other
     channels would tell an open page about it. Without this the
     customer gets the email and lands on a page still saying "Nothing
     right now". */
  const handleCaseStateChange = useCallback(() => {
    if (!billId) return;
    loadCaseState(billId).catch(() => {});
    if (!user) return;
    supabase
      .from('bills')
      .select('*')
      .eq('id', billId)
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setBill(data as Bill);
      });
  }, [billId, user, loadCaseState]);

  useBillRealtime({
    billId: billId ?? null,
    onNewUpdate: handleNewUpdate,
    onNewCommLog: handleNewCommLog,
    onNewChatMessage: handleNewChatMessage,
    onCaseStateChange: handleCaseStateChange,
  });

  /* ── The thread: people only, OLDEST-first ──
     Status updates and admin system rows ("Status updated: A → B") are
     excluded entirely — the stage pill, progress rail, and Details
     sheet carry case progress, so the conversation stays a
     conversation. */
  const feed = useMemo<FeedEvent[]>(() => {
    return messages
      // Admin messages deleted before the customer read them are left out
      // completely. Their own deleted messages still show as "deleted".
      .filter(
        (m) =>
          m.sender_type !== 'system' &&
          m.is_internal !== true &&
          !(m.sender_type === 'admin' && m.deleted_at),
      )
      .map((m) => ({ kind: 'message' as const, row: m, ts: m.created_at }))
      .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime());
  }, [messages]);

  /* Keep the newest entry in view: on load and whenever the feed grows,
     jump the scroll container to the bottom. Closed cases are the one
     exception, they open at the top so the result is the first thing
     seen, with the conversation below it. */
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const closed = bill
      ? (bill.case_stage ? bill.case_stage === 'resolved' : bill.status === 'completed')
      : false;
    if (closed) return;
    el.scrollTop = el.scrollHeight;
  }, [feed.length, loading, bill]);

  /* The id of the most recent customer-sent, non-deleted message.
     Receipt indicators only render on this one so the feed isn't
     littered with repeated "Sent" labels. */
  const latestOwnMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.sender_type === 'customer' && !m.deleted_at) return m.id;
    }
    return null;
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    /* Enter to send, Shift+Enter for a newline, same UX as
       Dashboard.tsx's existing chat. */
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /* Composer grows with the draft (up to ~5 lines) and collapses back
     once it's sent or cleared. */
  const autogrowComposer = () => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  };
  useEffect(() => {
    if (!draft) {
      const el = composerRef.current;
      if (el) el.style.height = 'auto';
    }
  }, [draft]);

  /* ── Edit / soft-delete own messages ──
     RLS (migration 034) only allows UPDATE on own customer text messages
     younger than 15 min. The frontend pre-checks the same conditions so
     the affordance only shows when it'll actually succeed. */
  const EDIT_WINDOW_MS = 15 * 60 * 1000;
  const canEditMessage = useCallback(
    (m: ChatMessageRow) => {
      if (!user) return false;
      if (m.sender_type !== 'customer') return false;
      if ((m.sender_id ?? '') !== user.id) return false;
      if (m.attachment_url || (m.attachments && m.attachments.length > 0)) return false;
      if (m.deleted_at) return false;
      const ageMs = Date.now() - new Date(m.created_at).getTime();
      return ageMs < EDIT_WINDOW_MS;
    },
    [user],
  );

  const beginEditMessage = useCallback((m: ChatMessageRow) => {
    setEditingMessageId(m.id);
    setEditDraft(m.message ?? '');
  }, []);

  const cancelEditMessage = useCallback(() => {
    setEditingMessageId(null);
    setEditDraft('');
  }, []);

  const saveEditMessage = useCallback(async () => {
    if (!editingMessageId) return;
    const original = messages.find((x) => x.id === editingMessageId);
    if (!original) return;
    const next = editDraft.trim();
    if (!next || next === (original.message ?? '')) {
      cancelEditMessage();
      return;
    }
    const { data, error } = await supabase
      .from('chat_messages')
      .update({ message: next, edited_at: new Date().toISOString() })
      .eq('id', editingMessageId)
      .select('*')
      .single();
    if (error) {
      console.error('edit message failed', error);
      /* PG code 42703 = column does not exist (e.g. migration 050 not yet
         applied). Otherwise it's almost certainly the 15-min RLS gate. */
      const isMissingCol =
        (error.code === '42703') ||
        (typeof error.message === 'string' && error.message.includes('column'));
      showToast(
        isMissingCol
          ? 'Save failed. The chat-message edit migration may not be applied yet.'
          : 'Couldn\'t save. Only your own messages from the last 15 minutes can be edited.',
        'error',
      );
      return;
    }
    if (data) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === editingMessageId ? (data as ChatMessageRow) : m,
        ),
      );
    }
    cancelEditMessage();
  }, [editingMessageId, editDraft, messages, showToast, cancelEditMessage]);

  const deleteMessage = useCallback(
    async (m: ChatMessageRow) => {
      if (!canEditMessage(m)) return;
      /* Soft delete: clear text + set deleted_at. RLS allows this within
         15 min via the same policy that gates edits. */
      const { data, error } = await supabase
        .from('chat_messages')
        .update({ message: null, deleted_at: new Date().toISOString() })
        .eq('id', m.id)
        .select('*')
        .single();
      if (error) {
        console.error('delete message failed', error);
        const isMissingCol =
          (error.code === '42703') ||
          (typeof error.message === 'string' && error.message.includes('column'));
        showToast(
          isMissingCol
            ? 'Delete failed. Migration 050 (chat_messages.deleted_at) may not be applied yet.'
            : 'Couldn\'t delete. Only your own messages from the last 15 minutes can be removed.',
          'error',
        );
        return;
      }
      if (data) {
        setMessages((prev) =>
          prev.map((x) => (x.id === m.id ? (data as ChatMessageRow) : x)),
        );
      }
      if (editingMessageId === m.id) cancelEditMessage();
    },
    [canEditMessage, editingMessageId, cancelEditMessage, showToast],
  );

  /* ── Documents ── */

  /* Download — fetch a short-lived signed URL for the original bill or
     any attachment, then trigger a browser download. createSignedUrl
     respects the bucket's RLS, so we don't need to gate again client-side. */
  const handleDownload = useCallback(
    async (filePath: string, displayName?: string | null) => {
      if (!filePath) return;
      const { data, error } = await supabase
        .storage
        .from('medical_bills')
        .createSignedUrl(filePath, 60); // 60s window is plenty for a click
      if (error || !data?.signedUrl) {
        phFailure({
          step: 'document_download_signed_url',
          message: error?.message,
        });
        showToast('Could not generate download link', 'error');
        return;
      }
      /* Force a download (not just an in-tab preview) by anchoring */
      /* 2026-08-08: downloads were untracked. This is the customer taking
         delivery of the thing they paid for (the bill, the negotiated letter,
         a case attachment) and there was no event for it. */
      phDocumentDownloaded({
        billId,
        documentType: 'case_file',
        fileName: displayName || filePath.split('/').pop() || undefined,
      });
      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = displayName || filePath.split('/').pop() || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [showToast, billId],
  );

  /* For attachments stored as full URLs (which the upload step below
     produces via getPublicUrl), we open them directly. They live in the
     same bucket, scoped under userId/billId/, so the bucket's RLS still
     enforces ownership when needed. */
  const handleOpenAttachment = useCallback((url: string, name: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  /* In-page preview (2026-09-19). Clicking a document used to trigger a
     download, which on a phone dumps the customer into the Files app and
     on desktop into another tab. Now the row opens a small viewer in the
     same window: the PDF or image, the name, and a Download button for
     the people who do want the file. The signed URL lives ten minutes,
     long enough to read a bill, short enough not to leak. */
  type DocPreview = {
    name: string;
    meta: string;
    url: string;
    kind: 'pdf' | 'image' | 'other';
    download: () => void;
  };
  const [docPreview, setDocPreview] = useState<DocPreview | null>(null);
  const [docPreviewLoading, setDocPreviewLoading] = useState(false);

  /* The display name is whatever the customer typed ("ucla"), so the
     extension has to come off the storage path or the URL instead. */
  const previewKind = (mime: string | null | undefined, ...names: (string | null | undefined)[]): DocPreview['kind'] => {
    if (mime === 'application/pdf') return 'pdf';
    if (mime?.startsWith('image/')) return 'image';
    for (const n of names) {
      if (!n) continue;
      const ext = n.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase() ?? '';
      if (ext === 'pdf') return 'pdf';
      if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic'].includes(ext)) return 'image';
    }
    return 'other';
  };

  const openPreview = useCallback(
    async (opts: { name: string; meta: string; path?: string | null; url?: string | null; mime?: string | null; download: () => void }) => {
      setDocPreviewLoading(true);
      try {
        /* Attachments are stored as public-style URLs into the private
           bucket, so they need signing the same way the chat ones do.
           Anything we cannot place in the bucket is shown as it is. */
        const path = opts.path || (opts.url ? bucketPath('medical_bills', opts.url) : null);
        let url = opts.url || '';
        if (path) {
          const { data, error } = await supabase.storage
            .from('medical_bills')
            .createSignedUrl(path, 600);
          if (error || !data?.signedUrl) {
            phFailure({ step: 'document_preview_signed_url', message: error?.message });
            showToast('Could not open that document', 'error');
            return;
          }
          url = data.signedUrl;
        }
        if (!url) return;
        setDocPreview({
          name: opts.name,
          meta: opts.meta,
          url,
          kind: previewKind(opts.mime, opts.path, opts.url, opts.name),
          download: opts.download,
        });
      } finally {
        setDocPreviewLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showToast],
  );

  /* Chat attachments need fresh signed URLs. Customer uploads live in the
     private medical_bills bucket and are stored as public-style URLs, which
     the browser can't fetch directly. Admin-sent files live in the
     chat-attachments bucket and the row holds a signed URL that was minted
     at upload time with a 7 day expiry, so after a week it's dead. Sign
     both once per page load (the customer's own RLS applies) and keep the
     result in a map keyed by the original URL. */
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const bucketPath = (bucket: string, url: string): string | null => {
    const marker = `/${bucket}/`;
    const i = url.indexOf(marker);
    if (i === -1) return null;
    try {
      return decodeURIComponent(url.slice(i + marker.length).split('?')[0]);
    } catch {
      return null;
    }
  };
  useEffect(() => {
    const wanted: { url: string; bucket: string; path: string }[] = [];
    for (const m of messages) {
      const atts = m.attachments && m.attachments.length > 0
        ? m.attachments
        : m.attachment_url
        ? [{ url: m.attachment_url, name: m.attachment_name || '', type: m.attachment_type || '' }]
        : [];
      for (const a of atts) {
        if (!a.url || signedUrls[a.url]) continue;
        for (const bucket of ['medical_bills', 'chat-attachments']) {
          const path = bucketPath(bucket, a.url);
          if (path) {
            wanted.push({ url: a.url, bucket, path });
            break;
          }
        }
      }
    }
    if (wanted.length === 0) return;
    let cancelled = false;
    const buckets = [...new Set(wanted.map((w) => w.bucket))];
    Promise.all(
      buckets.map(async (bucket) => {
        const batch = wanted.filter((w) => w.bucket === bucket);
        const { data } = await supabase.storage
          .from(bucket)
          .createSignedUrls(batch.map((w) => w.path), 3600);
        const next: Record<string, string> = {};
        (data || []).forEach((row, i) => {
          if (row.signedUrl) next[batch[i].url] = row.signedUrl;
        });
        return next;
      })
    ).then((results) => {
      if (cancelled) return;
      const next = Object.assign({}, ...results) as Record<string, string>;
      if (Object.keys(next).length > 0) setSignedUrls((prev) => ({ ...prev, ...next }));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);
  const viewUrl = (url: string) => signedUrls[url] || url;

  /* Which files the composer accepts. Shared by the Documents rail and
     the chat clip so the two never drift apart. */
  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
  const MAX_FILE_BYTES = 10 * 1024 * 1024;

  const filterAcceptedFiles = useCallback(
    (files: FileList | File[] | null): File[] => {
      if (!files) return [];
      return Array.from(files).filter((f) => {
        if (!ALLOWED_TYPES.includes(f.type)) {
          showToast(`${f.name}: only PDF, JPG, PNG allowed`, 'error');
          return false;
        }
        if (f.size > MAX_FILE_BYTES) {
          showToast(`${f.name}: max 10 MB`, 'error');
          return false;
        }
        return true;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showToast],
  );

  /* Push files to the medical_bills bucket. Returns the ones that made
     it, already shaped for bills.attachments. */
  const uploadFiles = useCallback(
    async (files: File[]): Promise<Attachment[]> => {
      if (!user || !bill) return [];
      const done: Attachment[] = [];
      for (const picked of files) {
        // HEIC photos (even ones named .jpg) become real JPEGs here, so
        // they preview in Chrome and the admin. Anything else is unchanged.
        const file = await prepareImageForUpload(picked);
        const ext = file.name.split('.').pop() ?? 'bin';
        /* Storage occasionally returns a transient 5xx (seen in prod
           2026-07-29: HTTP 544 DatabaseTimeout during a brief DB brownout,
           which surfaced to customers as "upload failed"). Retry up to
           3 attempts with a short backoff before giving up. A fresh path
           per attempt avoids a 409 if a timed-out attempt actually wrote
           the object. */
        let uploadedPath: string | null = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          if (attempt > 1) {
            await new Promise((r) => setTimeout(r, 1500 * (attempt - 1)));
          }
          const path = `${user.id}/${bill.id}/attachments/${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 8)}.${ext}`;
          const { error: upErr } = await supabase.storage
            .from('medical_bills')
            .upload(path, file, { contentType: file.type, upsert: false });
          if (!upErr) {
            uploadedPath = path;
            break;
          }
          console.error(`upload error (attempt ${attempt}/3)`, upErr);
        }
        if (!uploadedPath) {
          showToast(
            `${file.name}: upload failed. This is usually temporary, please try again in a few minutes.`,
            'error',
          );
          continue;
        }
        const { data: pub } = supabase.storage
          .from('medical_bills')
          .getPublicUrl(uploadedPath);
        done.push({
          url: pub.publicUrl,
          name: file.name,
          type: file.type,
          uploaded_at: new Date().toISOString(),
        });
      }
      return done;
    },
    [user, bill, showToast],
  );

  /* Save the grown attachments array through the RPC (customers can't
     UPDATE bills directly because of RLS, see migration 049). Since
     migration 098 the RPC also writes the chat row for the new files,
     with whatever text came with them, so the thread shows the upload
     on both sides. We pull that row back right away rather than wait
     on realtime, same reason as handleSend. */
  const persistAttachments = useCallback(
    async (newOnes: Attachment[], text: string | null): Promise<boolean> => {
      if (!bill || !user) return false;
      const merged = [...attachments, ...newOnes];
      const { error: rpcErr } = await supabase.rpc('update_bill_attachments', {
        bill_id: bill.id,
        new_attachments: merged,
        message: text && text.trim() ? text.trim() : null,
      });
      if (rpcErr) {
        console.error('persist attachments error', rpcErr);
        showToast("Couldn't save attachments, please try again", 'error');
        return false;
      }
      setAttachments(merged);

      const { data: posted } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('bill_id', bill.id)
        .eq('sender_type', 'customer')
        .eq('source', 'upload')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (posted) {
        setMessages((prev) =>
          prev.find((m) => m.id === (posted as ChatMessageRow).id)
            ? prev
            : [...prev, posted as ChatMessageRow],
        );
      }

      /* Mirror the RPC's server-side bookkeeping (migration 084): an
         upload answers the open request and lifts a quiet-case pause. */
      setActiveRequest((prev) =>
        prev && (prev.status === 'open' || prev.status === 'paused')
          ? { ...prev, status: 'response_received', responded_at: new Date().toISOString() }
          : prev,
      );
      setBill((prev) =>
        prev && prev.pause_reason === 'customer_unresponsive'
          ? { ...prev, paused_at: null, pause_reason: null }
          : prev,
      );
      return true;
    },
    [bill, user, attachments, showToast],
  );

  /* "Attach more" in the Documents rail: upload straight away, no
     text. The file lands in Documents and, via the RPC, in the thread. */
  const handleAttachFiles = useCallback(
    async (files: FileList | null) => {
      if (!user || !bill) return;
      const acceptedFiles = filterAcceptedFiles(files);
      if (acceptedFiles.length === 0) return;

      setUploadingDoc(true);
      const newOnes = await uploadFiles(acceptedFiles);
      if (newOnes.length > 0) {
        const ok = await persistAttachments(newOnes, null);
        if (ok) {
          showToast(
            newOnes.length === 1 ? 'Document attached' : `${newOnes.length} documents attached`,
          );
          phCaseMessageSent({ billId: bill.id, hasAttachment: true });
        }
      }
      setUploadingDoc(false);
    },
    [user, bill, filterAcceptedFiles, uploadFiles, persistAttachments, showToast],
  );

  /* The clip in the composer. Files wait in a strip above the text box
     until Send, so a customer can say "here's the email they sent me"
     and attach it in one message instead of two. */
  const chatFileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const pendingPreviews = useMemo(
    () => pendingFiles.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)),
    [pendingFiles],
  );
  useEffect(
    () => () => {
      pendingPreviews.forEach((u) => u && URL.revokeObjectURL(u));
    },
    [pendingPreviews],
  );
  const handlePickChatFiles = useCallback(
    (files: FileList | null) => {
      const accepted = filterAcceptedFiles(files);
      if (accepted.length === 0) return;
      setPendingFiles((prev) => {
        const next = [...prev, ...accepted].slice(0, 5);
        if (prev.length + accepted.length > 5) showToast('Up to 5 files per message', 'error');
        return next;
      });
      composerRef.current?.focus();
    },
    [filterAcceptedFiles, showToast],
  );
  const removePendingFile = useCallback((idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  /* ── Send a chat message ──
     Text only: insert + select the row back so we can locally append
     immediately instead of waiting on the realtime channel (it
     sometimes lags by a few seconds, which made the bubble feel like it
     had vanished). The realtime listener's id-dedupe means we won't
     double up even if it also fires for our own write.
     With files: upload them, then one RPC call saves them to Documents
     and posts the message with the files attached (migration 098). */
  const handleSend = async () => {
    const text = draft.trim();
    const files = pendingFiles;
    if ((!text && files.length === 0) || !user || !billId || sending) return;
    setSending(true);
    try {
      if (files.length > 0) {
        setUploadingDoc(true);
        const newOnes = await uploadFiles(files);
        if (newOnes.length === 0) return;
        const ok = await persistAttachments(newOnes, text);
        if (!ok) return;
        setPendingFiles([]);
        phCaseMessageSent({ billId, hasAttachment: true });
      } else {
        const { data, error } = await supabase
          .from('chat_messages')
          .insert({
            bill_id: billId,
            sender_type: 'customer',
            sender_id: user.id,
            sender_name: null,
            message: text,
          })
          .select('*')
          .single();
        if (error) throw error;
        if (data) {
          setMessages((prev) =>
            prev.find((m) => m.id === (data as ChatMessageRow).id)
              ? prev
              : [...prev, data as ChatMessageRow],
          );
        }
        /* Customer-initiated replies are the clearest engagement signal on an
           open case and were untracked. */
        phCaseMessageSent({ billId, hasAttachment: false });
        /* Mirror the DB trigger optimistically: a reply flips the open
           request to response_received and lifts a quiet-case pause, so
           the banner updates the instant they hit send instead of on the
           next refetch. */
        setActiveRequest((prev) =>
          prev && (prev.status === 'open' || prev.status === 'paused')
            ? { ...prev, status: 'response_received', responded_at: new Date().toISOString() }
            : prev,
        );
        setBill((prev) =>
          prev && prev.pause_reason === 'customer_unresponsive'
            ? { ...prev, paused_at: null, pause_reason: null }
            : prev,
        );
      }
      setDraft('');
      composerRef.current?.focus();
    } catch (err) {
      console.error('Failed to send message', err);
      showToast('Failed to send message', 'error');
    } finally {
      setUploadingDoc(false);
      setSending(false);
    }
  };

  const handleRemoveAttachment = useCallback(
    async (idx: number) => {
      if (!bill) return;
      const next = attachments.filter((_, i) => i !== idx);
      const previous = attachments;
      setAttachments(next); // optimistic
      const { error } = await supabase.rpc('update_bill_attachments', {
        bill_id: bill.id,
        new_attachments: next,
      });
      if (error) {
        console.error('remove attachment error', error);
        setAttachments(previous);
        showToast('Couldn\'t remove that one', 'error');
      }
    },
    [bill, attachments, showToast],
  );

  /* ── Derived display ── */
  const billAmount = Number(bill?.bill_amount) || 0;
  const caseSub = [
    bill?.bill_number ? `Case #${bill.bill_number}` : null,
    billAmount > 0 ? `$${billAmount.toLocaleString()} bill` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  /* Case state v2: when the bill carries case_stage (migration 084),
     the progress bar renders the five v2 stages and the blockers
     ("intel needed", "waiting on you") stop masquerading as stages —
     they live in the Needed-from-you card instead. */
  const v2 = !!bill?.case_stage;
  const stageIndexV2 = useMemo(
    () => Math.max(0, STAGES_V2.findIndex((s) => s.key === bill?.case_stage)),
    [bill?.case_stage],
  );
  const isPaused = !!bill?.paused_at;
  const pausedForCustomer = isPaused && bill?.pause_reason === 'customer_unresponsive';
  const waitingOnLabel = v2 && bill?.waiting_on ? CASE_WAITING_ON_LABELS[bill.waiting_on] : null;
  /* "Needed from you: Nothing right now" unless an OPEN ask exists. */
  const openAsk = activeRequest?.status === 'open' || activeRequest?.status === 'paused'
    ? activeRequest
    : null;
  const answeredAsk = activeRequest?.status === 'response_received' ? activeRequest : null;
  const nextUpdateLabel = useMemo(() => {
    if (!v2 || !bill?.next_customer_update_at) return null;
    const d = new Date(bill.next_customer_update_at);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  }, [v2, bill?.next_customer_update_at]);

  /* ── Closed-case detection ──
     v2 cases close by reaching the `resolved` stage, whatever the
     outcome. Keying this off `status === 'completed'` (as this did)
     meant a case resolved as no_reduction, customer_withdrew or
     unable_to_proceed never got a final view at all — the customer was
     left staring at a progress bar on a case that had already ended. */
  const outcome = v2 ? bill?.case_outcome ?? null : null;
  const isClosed = v2 ? bill?.case_stage === 'resolved' : bill?.status === 'completed';
  /* Which claims the result block may make lives in ResultBlock now,
     outcome-aware there. The page only needs the closed date for the
     conversation's end stamp. */
  const closedDateLabel = useMemo(() => {
    if (!isClosed || !v2 || !bill?.case_stage_entered_at) return null;
    const d = new Date(bill.case_stage_entered_at);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  }, [isClosed, v2, bill?.case_stage_entered_at]);
  const resolutionPathLabel =
    v2 && bill?.resolution_path ? RESOLUTION_PATH_LABELS[bill.resolution_path] : null;
  const lastActivityLabel = useMemo(() => {
    if (!v2) return null;
    const stamps = [bill?.last_customer_activity_at, bill?.last_external_activity_at, bill?.case_stage_entered_at]
      .filter((s): s is string => !!s)
      .map((s) => new Date(s).getTime())
      .filter((t) => !Number.isNaN(t));
    if (stamps.length === 0) return null;
    return new Date(Math.max(...stamps)).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
    });
  }, [v2, bill?.last_customer_activity_at, bill?.last_external_activity_at, bill?.case_stage_entered_at]);

  /* Map the backend statuses down to the 4 user-facing stages.
     Legacy fallback only — kept until every row carries case_stage. */
  const stageIndex = useMemo(() => {
    switch (bill?.status) {
      case 'submitted':        return 0;
      case 'info_needed':      return 0; // back at file-intake
      case 'no_response':      return 0; // paused at intake until they reply
      case 'in_review':        return 1;
      case 'negotiating':      return 2;
      case 'negotiating_more': return 2;
      case 'financial_assistance': return 2; // assistance route, still mid-pursuit
      case 'completed':        return 3;
      default:                 return 0;
    }
  }, [bill?.status]);

  const stageLabel = v2
    ? (isPaused ? 'Paused' : CASE_STAGE_LABELS[(bill?.case_stage ?? 'received') as CaseStage])
    : bill?.status === 'no_savings' ? 'Closed' :
      bill?.status === 'canceled' ? 'Withdrawn' :
      bill?.status === 'financial_assistance' ? 'Financial assistance' :
      bill?.status === 'info_needed' ? 'Intel needed' :
      bill?.status === 'no_response' ? 'Waiting on you' :
      STAGES[stageIndex];

  const specialistFirstName = specialistName?.split(' ')[0] ?? null;

  /* Quick-reply suggestions, status-aware. An open ask wins: replying
     to it is the one thing that unblocks the case. */
  const quickChips =
    openAsk
      ? [
          "I've sent over what you asked for",
          'I have a question about what you need',
          'I need a few more days',
        ]
      : bill?.status === 'info_needed'
      ? [
          "I've sent over the documents you asked for",
          'What else do you need from me?',
          'What are the next steps for my case?',
        ]
      : bill?.status === 'negotiating' || bill?.status === 'negotiating_more'
      ? [
          'Any update from the provider?',
          'How long does this take?',
          'I heard back from the provider',
        ]
      : bill?.status === 'completed'
      ? [
          'Thank you!',
          'Question about the final amount',
          'How do I pay this?',
        ]
      : [
          'What happens next?',
          'How long should this take?',
          'Question about my bill',
        ];

  /* Shared documents block — right rail on desktop, bottom sheet on
     mobile. The hidden file input lives once at the page root; both
     Attach buttons trigger it. */
  const documentsBlock = (
    <>
      {bill?.file_path && (
        <button
          type="button"
          className="cp2-doc"
          disabled={docPreviewLoading}
          onClick={() =>
            openPreview({
              name: bill.display_name || 'Medical bill',
              meta: `Original · ${bill.file_path.split('.').pop()?.toUpperCase()}`,
              path: bill.file_path,
              download: () => handleDownload(bill.file_path, bill.display_name),
            })
          }
          title="Open original bill"
        >
          <span className="cp2-doc-icon"><FileText size={15} /></span>
          <span className="cp2-doc-body">
            <span className="cp2-doc-name">{bill.display_name || 'Medical bill'}</span>
            <span className="cp2-doc-meta">Original · {bill.file_path.split('.').pop()?.toUpperCase()}</span>
          </span>
          <Eye size={14} className="cp2-doc-dl" aria-hidden />
        </button>
      )}
      {attachments.map((att, i) => (
        <div key={`${att.url}-${i}`} className="cp2-doc-row">
          <button
            type="button"
            className="cp2-doc"
            disabled={docPreviewLoading}
            onClick={() =>
              openPreview({
                name: att.name,
                meta: att.type ? att.type.split('/').pop()?.toUpperCase() ?? 'FILE' : 'FILE',
                url: att.url,
                mime: att.type,
                download: () => handleOpenAttachment(att.url, att.name),
              })
            }
            title={`Open ${att.name}`}
          >
            <span className="cp2-doc-icon cp2-doc-icon--soft"><FileText size={15} /></span>
            <span className="cp2-doc-body">
              <span className="cp2-doc-name">{att.name}</span>
              <span className="cp2-doc-meta">
                {att.type ? att.type.split('/').pop()?.toUpperCase() : 'FILE'}
              </span>
            </span>
            <Eye size={14} className="cp2-doc-dl" aria-hidden />
          </button>
          <button
            type="button"
            className="cp2-doc-remove"
            onClick={() => handleRemoveAttachment(i)}
            aria-label={`Remove ${att.name}`}
            title="Remove"
          >
            <XIcon size={12} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="cp2-attach-more"
        onClick={() => docInputRef.current?.click()}
        disabled={uploadingDoc}
      >
        <Plus size={14} />
        {uploadingDoc ? 'Uploading…' : 'Attach more'}
      </button>
      <p className="cp2-attach-hint">PDF, JPG, or PNG, up to 10 MB each</p>
    </>
  );

  /* Vertical progress steps — right rail + mobile sheet. v2 renders
     the five case stages; the legacy 4-step rail stays as the
     pre-migration fallback. */
  const railStages: { label: string; sub?: string }[] = v2
    ? STAGES_V2.map((s) => ({ label: s.label }))
    : STAGES.map((label) => ({ label }));
  const railIndex = v2 ? stageIndexV2 : stageIndex;

  /* One line of proof of work, above the steps. No findings, no
     dollars: what was read, what it was checked against, who has it. */
  const receiptLine = receipt ? (
    <div className="cp2-receipt">
      <img src="/images/mascot/fox-avatar.png" alt="" className="cp2-receipt-fox" />
      <p className="cp2-receipt-text">
        <strong>Our AI</strong> read your bill
        {receipt.line_items > 0 ? ` · ${receipt.line_items} line item${receipt.line_items === 1 ? '' : 's'}` : ''}
        {receipt.checks ? ` · checked against ${receipt.checks} rules and price benchmarks` : ' · checked against our rules and price benchmarks'}
        {isClosed ? '' : receipt.drafted_at ? ' · a specialist has the plan' : ' · a specialist is on it'}
      </p>
    </div>
  ) : null;

  const progressSteps = (
    <ol className="cp2-steps" aria-label="Case progress">
      {railStages.map((stage, i) => {
        const done = i < railIndex || (v2 && bill?.case_stage === 'resolved' && i === railIndex);
        const now = i === railIndex && !done;
        const last = i === railStages.length - 1;
        /* ⚠️ The final step must not promise an outcome. The legacy
           rail said "You keep the savings" on every case, including
           ones that end with no reduction or that the customer asked
           us to drop — a promise made before anyone knows the result.
           v2 states the outcome once it exists and stays neutral
           until then. */
        const sub =
          i === 0
            ? bill
              ? new Date(bill.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
              : ''
            : last && v2 && isClosed
            ? /* The final step of a RESOLVED case names the result — or
                 says honestly that a historical one is still being
                 confirmed — rather than a bare "Done". */
              outcome
              ? CASE_OUTCOME_LABELS[outcome]
              : 'Confirming the historical result'
            : now
            ? isPaused
              ? 'Paused for now'
              : 'Underway now'
            : done
            ? 'Done'
            : last
            ? v2
              ? 'However it lands, we tell you straight'
              : 'You keep the savings'
            : 'Up next';
        return (
          <li key={stage.label} className={`cp2-step ${done ? 'is-done' : now ? 'is-now' : ''}`} aria-current={now ? 'step' : undefined}>
            <span className="cp2-step-rail">
              <span className="cp2-step-dot">{done ? <Check size={9} strokeWidth={3.5} /> : null}</span>
              {i < railStages.length - 1 && <span className="cp2-step-line" aria-hidden />}
            </span>
            <span className="cp2-step-body">
              <span className="cp2-step-title">{stage.label}</span>
              <span className="cp2-step-sub">{sub}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );

  /* v2 status lines under the progress rail: who the ball is with,
     what (if anything) is needed from the customer, and the next
     promised update. The exact shape the redesign specifies. */
  const caseMetaBlock = v2 ? (
    <dl className="cp2-meta" aria-label="Case status">
      {/* On a resolved case the outcome is the only fact that matters;
          waiting-on and next-update are noise once it's over. */}
      {isClosed ? (
        outcome && (
          <div className="cp2-meta-row">
            <dt>Outcome</dt>
            <dd>{CASE_OUTCOME_LABELS[outcome]}</dd>
          </div>
        )
      ) : (
        <>
          {waitingOnLabel && (
            <div className="cp2-meta-row">
              <dt>Waiting on</dt>
              <dd>{waitingOnLabel}</dd>
            </div>
          )}
          <div className="cp2-meta-row">
            <dt>Needed from you</dt>
            <dd>{openAsk ? openAsk.title : answeredAsk ? 'Nothing more, we got your reply' : 'Nothing right now'}</dd>
          </div>
          {resolutionPathLabel && (
            <div className="cp2-meta-row">
              <dt>What we&apos;re doing</dt>
              <dd>{resolutionPathLabel}</dd>
            </div>
          )}
          {nextUpdateLabel && !isPaused && (
            <div className="cp2-meta-row">
              <dt>Next update by</dt>
              <dd>{nextUpdateLabel}</dd>
            </div>
          )}
        </>
      )}
      {lastActivityLabel && (
        <div className="cp2-meta-row">
          <dt>Last activity</dt>
          <dd>{lastActivityLabel}</dd>
        </div>
      )}
    </dl>
  ) : null;

  /* Closed-case rail: the settled facts, then what the customer can
     still do. Replaces the progress steps once the journey is over. */
  const resultRail = isClosed && bill ? (
    <dl className="cp2-meta" aria-label="Final result">
      <div className="cp2-meta-row">
        <dt>Outcome</dt>
        <dd>
          {outcome
            ? CASE_OUTCOME_LABELS[outcome]
            : v2
            ? 'Being confirmed'
            : (Number(bill.amount_saved) || 0) > 0
            ? 'Savings secured'
            : 'Closed'}
        </dd>
      </div>
      {(Number(bill.amount_saved) || 0) > 0 && (
        <div className="cp2-meta-row">
          <dt>You saved</dt>
          <dd className="cp2-meta-good num">${Number(bill.amount_saved).toLocaleString()}</dd>
        </div>
      )}
      {closedDateLabel && (
        <div className="cp2-meta-row">
          <dt>Closed</dt>
          <dd>{closedDateLabel}</dd>
        </div>
      )}
    </dl>
  ) : null;

  /* The message feed, rendered once and used by both the open and the
     closed layouts (the closed page shows it under the result). */
  const feedMessages = (() => {
                        let lastBucket = '';
                        return feed.map((ev) => {
                          const bucket = dayBucket(ev.ts);
                          const showDivider = bucket !== lastBucket;
                          lastBucket = bucket;

                          const divider = showDivider ? (
                            <div className="cp2-day">{bucket}</div>
                          ) : null;

                          const m = ev.row;
                          const fromCustomer = m.sender_type === 'customer';
                          const isDeleted = !!m.deleted_at;
                          const editable = canEditMessage(m);
                          const isEditing = editingMessageId === m.id;
                          const senderLabel = fromCustomer
                            ? null
                            : m.sender_name ?? specialistName ?? 'Case specialist';
                          const msgFiles: { url: string; name: string; type: string }[] =
                            m.attachments && m.attachments.length > 0
                              ? m.attachments
                              : m.attachment_url
                              ? [{ url: m.attachment_url, name: m.attachment_name || '', type: m.attachment_type || '' }]
                              : [];

                          return (
                            <div key={`m-${m.id}`}>
                              {divider}
                              <div className={`cp2-msg ${fromCustomer ? 'is-me' : 'is-them'} ${isDeleted ? 'is-deleted' : ''}`}>
                                {!fromCustomer && (
                                  <div className="cp2-msg-avatar" aria-hidden>
                                    <img src="/images/mascot/fox-avatar.png" alt="" />
                                  </div>
                                )}
                                <div className="cp2-msg-col">
                                  {isEditing ? (
                                    <div className="cp2-edit">
                                      <textarea
                                        className="cp2-edit-input"
                                        value={editDraft}
                                        autoFocus
                                        onChange={(e) => setEditDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Escape') {
                                            e.preventDefault();
                                            cancelEditMessage();
                                          }
                                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                            e.preventDefault();
                                            saveEditMessage();
                                          }
                                        }}
                                        rows={2}
                                      />
                                      <div className="cp2-edit-actions">
                                        <button type="button" className="cp2-edit-btn" onClick={cancelEditMessage}>
                                          Cancel
                                        </button>
                                        <button
                                          type="button"
                                          className="cp2-edit-btn cp2-edit-btn--save"
                                          onClick={saveEditMessage}
                                          disabled={!editDraft.trim() || editDraft.trim() === (m.message ?? '')}
                                        >
                                          Save
                                        </button>
                                      </div>
                                    </div>
                                  ) : isDeleted ? (
                                    <div className="cp2-bubble cp2-bubble--deleted">
                                      <em>This message was deleted</em>
                                    </div>
                                  ) : (
                                    m.message && <div className="cp2-bubble">{m.message}</div>
                                  )}

                                  {!isDeleted && msgFiles.length > 0 && (
                                    <div className={`cp2-msg-files ${msgFiles.length > 1 ? 'is-grid' : ''}`}>
                                      {msgFiles.map((att, i) =>
                                        att.type?.startsWith('image/') ? (
                                          <a
                                            key={`${att.url}-${i}`}
                                            className="cp2-msg-img"
                                            href={viewUrl(att.url)}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={att.name}
                                          >
                                            <img src={viewUrl(att.url)} alt={att.name || 'Attachment'} loading="lazy" />
                                          </a>
                                        ) : (
                                          <a
                                            key={`${att.url}-${i}`}
                                            className="cp2-msg-attach"
                                            href={viewUrl(att.url)}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            <FileText size={13} />
                                            <span className="cp2-msg-attach-name">{att.name || 'Attachment'}</span>
                                          </a>
                                        ),
                                      )}
                                    </div>
                                  )}
                                  {!isDeleted && m.source === 'upload' && (
                                    <div className="cp2-msg-note">
                                      <Check size={11} /> Added to your documents
                                    </div>
                                  )}

                                  {/* Meta line: sender · time · edited · receipt · edit/delete */}
                                  <div className="cp2-msg-meta">
                                    {senderLabel && <span className="cp2-msg-sender">{senderLabel} · </span>}
                                    <span className="num">{formatTime(m.created_at)}</span>
                                    {m.edited_at && !isDeleted && <span> · edited</span>}
                                    {fromCustomer && !isDeleted && m.id === latestOwnMessageId && (
                                      <span
                                        className={`cp2-receipt ${m.read_at ? 'is-read' : ''}`}
                                        title={
                                          m.read_at
                                            ? `Read ${formatTime(m.read_at)}`
                                            : 'Sent, your specialist hasn\'t opened it yet'
                                        }
                                      >
                                        {' '}· {m.read_at ? 'Read' : 'Sent'}
                                      </span>
                                    )}
                                    {fromCustomer && !isDeleted && !isEditing && editable && (
                                      <span className="cp2-msg-actions">
                                        {' '}·{' '}
                                        <button type="button" className="cp2-msg-action" onClick={() => beginEditMessage(m)}>
                                          Edit
                                        </button>{' '}
                                        ·{' '}
                                        <button type="button" className="cp2-msg-action cp2-msg-action--danger" onClick={() => deleteMessage(m)}>
                                          Delete
                                        </button>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        });
                      })();

  if (notFound) {
    return (
      <AppSidebar>
        <div className="case-page case-page-empty">
          <h2>Case not found.</h2>
          <p>This case doesn't exist or isn't yours to view.</p>
          <button onClick={() => navigate('/bills')} className="case-back-btn">
            Back to cases
          </button>
        </div>
      </AppSidebar>
    );
  }

  return (
    <AppSidebar>
      <div className="cp2">
        {/* Hidden file input, shared by every Attach affordance. */}
        <input
          type="file"
          ref={docInputRef}
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            handleAttachFiles(e.target.files);
            if (docInputRef.current) docInputRef.current.value = '';
          }}
        />
        {/* The composer's clip. Files wait in the strip until Send. */}
        <input
          type="file"
          ref={chatFileInputRef}
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            handlePickChatFiles(e.target.files);
            if (chatFileInputRef.current) chatFileInputRef.current.value = '';
          }}
        />

        {loading || !bill ? (
          <div className="case-loading" aria-busy="true">
            <div className="case-skeleton-hero" />
            <div className="case-skeleton-row" style={{ width: '94%' }} />
            <div className="case-skeleton-row" style={{ width: '70%' }} />
            <div className="case-skeleton-row" style={{ width: '85%' }} />
          </div>
        ) : (
          <div className="cp2-layout">
            {/* ── Sticky compact header ── */}
              <header className="cp2-topbar">
                <div className="cp2-topbar-row">
                  <button
                    type="button"
                    className="cp2-back"
                    onClick={() => navigate('/bills')}
                    aria-label="Back to cases"
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <div className="cp2-titlewrap">
                    <h1 className="cp2-title">
                      {caseTitle(bill)}
                    </h1>
                    <div className="cp2-subrow">
                      {billAmount > 0 && (
                        <span className="cp2-amount num">${billAmount.toLocaleString()} bill</span>
                      )}
                      <span className={`cp2-stage-pill ${isClosed || (v2 && bill.case_stage === 'resolved') ? 'is-closed' : ''}`}>
                        <span className="cp2-stage-dot" aria-hidden />
                        {stageLabel}
                      </span>
                      {isClosed && (Number(bill.amount_saved) || 0) > 0 && (
                        <span className="cp2-saved num">
                          Saved ${Number(bill.amount_saved || 0).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="cp2-ask-btn"
                    onClick={() => ask.open({ caseNumber: bill.bill_number ?? null })}
                    title="Ask Agent Loop about this case"
                  >
                    <Sparkles size={14} /> <span>Ask about this case</span>
                  </button>
                  <button
                    type="button"
                    className="cp2-details-btn"
                    onClick={() => setSheetOpen(true)}
                  >
                    <FolderOpen size={14} /> Details
                  </button>
                </div>
                {/* Hairline progress. v2: five segments, every journey
                    ends at Resolved so the bar always applies. Legacy:
                    four segments, hidden for no_savings and canceled
                    (those journeys ended off the main track). */}
                {v2 ? (
                  <div className="cp2-progress" aria-hidden>
                    {STAGES_V2.map((s, i) => (
                      <span
                        key={s.key}
                        className={
                          i < railIndex || bill.case_stage === 'resolved'
                            ? 'is-done'
                            : i === railIndex
                            ? 'is-active'
                            : ''
                        }
                      />
                    ))}
                  </div>
                ) : bill.status !== 'no_savings' && bill.status !== 'canceled' && (
                  <div className="cp2-progress" aria-hidden>
                    {STAGES.map((s, i) => (
                      <span
                        key={s}
                        className={
                          i < stageIndex ? 'is-done' : i === stageIndex ? 'is-active' : ''
                        }
                      />
                    ))}
                  </div>
                )}
              </header>

            <div className={`cp2-chat-col ${isClosed ? 'is-closed' : ''}`}>

              {isClosed ? (
                <>
                  <div className="cp2-thread cp2-thread--closed" ref={threadRef}>
                    <ResultBlock bill={bill} outcome={outcome} v2={v2} />
                    {feed.length > 0 && (
                      <>
                        <div className="cp2-conv-divider" role="separator">The conversation</div>
                        {feedMessages}
                        <div className="cp2-conv-divider" role="separator">
                          Case closed{closedDateLabel ? ` · ${closedDateLabel}` : ''}
                        </div>
                      </>
                    )}
                  </div>

                  {/* The composer survives the close. Messages still insert
                      through the same anon-key path; the DB-side v2 triggers
                      skip resolved cases, so writing here is safe and just
                      lands as a normal unread for ops. */}
                  <div className="cp2-composer-wrap">
                    {pendingFiles.length > 0 && (
                      <div className="cp2-pending" role="list" aria-label="Files to send">
                        {pendingFiles.map((f, i) => (
                          <div key={`${f.name}-${i}`} className="cp2-pending-item" role="listitem">
                            {pendingPreviews[i] ? (
                              <img src={pendingPreviews[i] as string} alt="" className="cp2-pending-thumb" />
                            ) : (
                              <span className="cp2-pending-thumb cp2-pending-thumb--file"><FileText size={16} /></span>
                            )}
                            <span className="cp2-pending-name">{f.name}</span>
                            <button
                              type="button"
                              className="cp2-pending-remove"
                              onClick={() => removePendingFile(i)}
                              aria-label={`Remove ${f.name}`}
                              disabled={sending}
                            >
                              <XIcon size={11} />
                            </button>
                          </div>
                        ))}
                        {sending && <span className="cp2-pending-status">Uploading…</span>}
                      </div>
                    )}
                    <div className="cp2-composer">
                      <button
                        type="button"
                        className={`cp2-clip ${pendingFiles.length > 0 ? 'has-files' : ''}`}
                        onClick={() => chatFileInputRef.current?.click()}
                        disabled={uploadingDoc || sending}
                        aria-label="Attach a file to this message"
                        title="Attach a file (PDF, JPG, PNG)"
                      >
                        <Paperclip size={16} />
                      </button>
                      <textarea
                        ref={composerRef}
                        className="cp2-input"
                        placeholder="This case is closed, but the thread isn't going anywhere. We still read it."
                        value={draft}
                        onChange={(e) => {
                          setDraft(e.target.value);
                          autogrowComposer();
                        }}
                        onKeyDown={handleKeyDown}
                        rows={1}
                      />
                      <button
                        type="button"
                        className="cp2-send"
                        onClick={handleSend}
                        disabled={(!draft.trim() && pendingFiles.length === 0) || sending}
                        aria-label="Send"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* ── Needed-from-you / paused strip ──
                      The one blocking fact about the case, pinned where
                      it can't be missed (most customers never open
                      Details on mobile). Replying or attaching below
                      clears it automatically — no email round trip. */}
                  {pausedForCustomer ? (
                    <div className="cp2-ask cp2-ask--paused" role="status">
                      <div className="cp2-ask-title">Your case is paused until we hear from you</div>
                      <div className="cp2-ask-body">
                        {openAsk ? openAsk.title : 'Reply below and we pick it right back up.'}
                      </div>
                    </div>
                  ) : openAsk ? (
                    <div className="cp2-ask" role="status">
                      <div className="cp2-ask-title">Needed from you</div>
                      <div className="cp2-ask-body">{openAsk.description || openAsk.title}</div>
                      {openAsk.why_needed && (
                        <div className="cp2-ask-why">Why: {openAsk.why_needed}</div>
                      )}
                      <div className="cp2-ask-hint">
                        {openAsk.request_type === 'document' ? 'Upload it here or reply below' : 'Reply below'}
                        {openAsk.due_at
                          ? `, ideally by ${new Date(openAsk.due_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}`
                          : ''}. The reminders stop on their own once it lands.
                      </div>
                      {/* Oct 2026: a document ask gets its own button, so nobody
                          has to find the paperclip in the composer. */}
                      {openAsk.request_type === 'document' && (
                        <button
                          type="button"
                          className="ml-btn ml-btn--ink cp2-ask-upload"
                          onClick={() => docInputRef.current?.click()}
                        >
                          <Paperclip size={14} /> Upload it
                        </button>
                      )}
                    </div>
                  ) : answeredAsk ? (
                    <div className="cp2-ask cp2-ask--done" role="status">
                      <div className="cp2-ask-title">Got it, thank you</div>
                      <div className="cp2-ask-body">
                        We received your reply about &ldquo;{answeredAsk.title}&rdquo; and we&apos;re on it.
                      </div>
                    </div>
                  ) : null}

                  {/* ── Thread ── */}
                  <div className="cp2-thread" ref={threadRef}>
                    {specialistName && (
                      <div className="cp2-intro">
                        <div className="cp2-intro-avatar" aria-hidden>
                          <img src="/images/mascot/fox-avatar.png" alt="" />
                        </div>
                        <div className="cp2-intro-body">
                          <div className="cp2-intro-name">
                            {specialistName} is on your case
                          </div>
                          <div className="cp2-intro-sub">
                            Your case specialist. Ask anything here,{' '}
                            {specialistFirstName} reads every message.
                          </div>
                        </div>
                      </div>
                    )}

                    {feed.length === 0 ? (
                      <div className="cp2-empty">
                        <img
                          src="/images/mascot/fox-reading-bill.png"
                          alt=""
                          className="cp2-empty-fox"
                        />
                        <div className="cp2-empty-title">
                          {openAsk ? 'We need one thing from you' : 'Your case is open'}
                        </div>
                        <div className="cp2-empty-body">
                          {openAsk
                            ? "It's in the box above. As soon as it's here, your specialist picks the case back up."
                            : 'Your specialist is reading the bill now. Their first message usually lands here within 24 hours.'}
                        </div>
                      </div>
                    ) : (
                      feedMessages
                    )}
                  </div>

                  {/* ── Composer, pinned bottom ── */}
                  <div className="cp2-composer-wrap">
                    <div className="cp2-chips" role="group" aria-label="Suggested replies">
                      {quickChips.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          className="cp2-chip"
                          onClick={() => {
                            setDraft(suggestion);
                            composerRef.current?.focus();
                          }}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                    {pendingFiles.length > 0 && (
                      <div className="cp2-pending" role="list" aria-label="Files to send">
                        {pendingFiles.map((f, i) => (
                          <div key={`${f.name}-${i}`} className="cp2-pending-item" role="listitem">
                            {pendingPreviews[i] ? (
                              <img src={pendingPreviews[i] as string} alt="" className="cp2-pending-thumb" />
                            ) : (
                              <span className="cp2-pending-thumb cp2-pending-thumb--file"><FileText size={16} /></span>
                            )}
                            <span className="cp2-pending-name">{f.name}</span>
                            <button
                              type="button"
                              className="cp2-pending-remove"
                              onClick={() => removePendingFile(i)}
                              aria-label={`Remove ${f.name}`}
                              disabled={sending}
                            >
                              <XIcon size={11} />
                            </button>
                          </div>
                        ))}
                        {sending && <span className="cp2-pending-status">Uploading…</span>}
                      </div>
                    )}
                    <div className="cp2-composer">
                      <button
                        type="button"
                        className={`cp2-clip ${pendingFiles.length > 0 ? 'has-files' : ''}`}
                        onClick={() => chatFileInputRef.current?.click()}
                        disabled={uploadingDoc || sending}
                        aria-label="Attach a file to this message"
                        title="Attach a file (PDF, JPG, PNG)"
                      >
                        <Paperclip size={16} />
                      </button>
                      <textarea
                        ref={composerRef}
                        className="cp2-input"
                        placeholder={
                          pendingFiles.length > 0
                            ? 'Add a note with your file (optional)…'
                            : specialistFirstName ? `Message ${specialistFirstName}…` : 'Write a reply…'
                        }
                        value={draft}
                        onChange={(e) => {
                          setDraft(e.target.value);
                          autogrowComposer();
                        }}
                        onKeyDown={handleKeyDown}
                        rows={1}
                      />
                      <button
                        type="button"
                        className="cp2-send"
                        onClick={handleSend}
                        disabled={(!draft.trim() && pendingFiles.length === 0) || sending}
                        aria-label="Send"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* ── Desktop right rail ── */}
            <aside className="cp2-side">
              {isClosed ? (
                <>
                  <div className="cp2-side-card">
                    <button
                      type="button"
                      className="cp2-side-toggle"
                      onClick={() => setJourneyOpen((v) => !v)}
                      aria-expanded={journeyOpen}
                    >
                      <span className="cp2-side-toggle-text">
                        <span className="cp2-side-title">Case progress</span>
                        {caseSub && <span className="cp2-side-sub num">{caseSub}</span>}
                      </span>
                      <ChevronDown size={15} className="cp2-side-chev" aria-hidden />
                    </button>
                    {receiptLine}
                    {journeyOpen && progressSteps}
                    {resultRail}
                  </div>
                  <div className="cp2-side-card">
                    <div className="cp2-side-title">Documents</div>
                    {documentsBlock}
                  </div>
                </>
              ) : (
                <>
                  <div className="cp2-side-card">
                    <div className="cp2-side-title">Case progress</div>
                    {caseSub && <div className="cp2-side-sub num">{caseSub}</div>}
                    {receiptLine}
                    {progressSteps}
                    {caseMetaBlock}
                  </div>
                  <div className="cp2-side-card">
                    <div className="cp2-side-title">Documents</div>
                    {documentsBlock}
                  </div>
                </>
              )}
            </aside>
          </div>
        )}

        {/* ── Mobile details sheet ── */}
        {sheetOpen && bill && (
          <>
            <div className="cp2-sheet-backdrop" onClick={() => setSheetOpen(false)} />
            <div className="cp2-sheet" role="dialog" aria-label="Case details">
              <div className="cp2-sheet-handle" aria-hidden />
              <div className="cp2-sheet-head">
                <div>
                  <div className="cp2-side-title">Case details</div>
                  {caseSub && <div className="cp2-side-sub num">{caseSub}</div>}
                </div>
                <button
                  type="button"
                  className="cp2-sheet-close"
                  onClick={() => setSheetOpen(false)}
                  aria-label="Close details"
                >
                  <XIcon size={16} />
                </button>
              </div>
              {receiptLine}
              {isClosed ? (
                <>
                  {progressSteps}
                  {resultRail}
                </>
              ) : (
                <>
                  {progressSteps}
                  {caseMetaBlock}
                </>
              )}
              <div className="cp2-side-title" style={{ marginTop: 18 }}>Documents</div>
              {documentsBlock}
            </div>
          </>
        )}

        {/* ── Document preview ── */}
        <Modal
          open={docPreview !== null}
          onClose={() => setDocPreview(null)}
          className="cp2-preview"
          priority
          showClose
          closeLabel="Close preview"
        >
          {docPreview && (
            <>
              <div className="cp2-preview-head">
                <span className="cp2-doc-icon"><FileText size={15} /></span>
                <div className="cp2-preview-titles">
                  <div className="cp2-preview-name">{docPreview.name}</div>
                  <div className="cp2-preview-meta">{docPreview.meta}</div>
                </div>
              </div>
              <div className="cp2-preview-body">
                {docPreview.kind === 'pdf' ? (
                  <iframe
                    src={`${docPreview.url}#toolbar=0&navpanes=0&view=FitH`}
                    title={docPreview.name}
                    className="cp2-preview-frame"
                  />
                ) : docPreview.kind === 'image' ? (
                  <img src={docPreview.url} alt={docPreview.name} className="cp2-preview-img" />
                ) : (
                  <p className="cp2-preview-none">
                    This file type can&apos;t be shown here. Download it to open it on your device.
                  </p>
                )}
              </div>
              <div className="cp2-preview-foot">
                <a
                  href={docPreview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cp2-preview-link"
                >
                  <ExternalLink size={13} /> Open full size
                </a>
                <button type="button" className="cp2-preview-download" onClick={docPreview.download}>
                  <Download size={13} /> Download
                </button>
              </div>
            </>
          )}
        </Modal>
      </div>
    </AppSidebar>
  );
}
